import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Nav,
  Tab,
  Button,
  Navbar,
  Table,
  Badge,
  Alert,
  Pagination,
  Modal,
  Form,
} from 'react-bootstrap';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { User, authService, adminService, AdminDashboard, Quiz } from '../services/api';
import AdminQuizList from './AdminQuizList';
import AdminAchievements from './AdminAchievements';
import AdminBroadcastPanel from './AdminBroadcastPanel';
import AdminVipPricing from './AdminVipPricing';
import AdminPokemonScraper from './AdminPokemonScraper';
import AdminCareerMissions from './AdminCareerMissions';
import AdminDailyMissions from './AdminDailyMissions';
import AdminReports from './AdminReports';
import QuestionManager from './QuestionManager';
import './AdminPanel.css';

const AdminPanel: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [dashboardData, setDashboardData] = useState<AdminDashboard | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const USERS_PAGE_SIZE = 50;
  const [usersPage, setUsersPage] = useState(1);
  const [usersTotalPages, setUsersTotalPages] = useState(1);
  const [usersTotalCount, setUsersTotalCount] = useState(0);
  const [usersHasNext, setUsersHasNext] = useState(false);
  const [usersHasPrev, setUsersHasPrev] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedQuiz, setSelectedQuiz] = useState<Quiz | null>(null);
  const [vipModalUser, setVipModalUser] = useState<User | null>(null);
  const [vipDaysInput, setVipDaysInput] = useState<string>('30');
  const [vipModalSubmitting, setVipModalSubmitting] = useState(false);
  const [vipModalError, setVipModalError] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Get token and userId from URL parameters
  const token = searchParams.get('token');
  const userId = searchParams.get('userId');

  useEffect(() => {
    const loadUserData = async () => {
      // Store token in localStorage if provided
      if (token) {
        localStorage.setItem('authToken', token);
      }

      try {
        // Fetch user data from API
        const userData = await authService.getProfile();
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
        
        // Check if user is admin
        if (userData.userType !== 'Admin' && userData.userType !== 3) {
          window.location.href = '/error?error=unauthorized&message=Access denied. Admin privileges required.';
          return;
        }

        // Load dashboard data
        const dashboard = await adminService.getDashboard();
        setDashboardData(dashboard);
      } catch {
        setError('Failed to load admin data');
        window.location.href = '/error?error=authentication_failed';
      } finally {
        setIsLoading(false);
      }
    };

    loadUserData();
  }, [token]);

  const loadUsers = useCallback(async () => {
    setIsLoadingUsers(true);
    try {
      const res = await adminService.getUsers({
        page: usersPage,
        pageSize: USERS_PAGE_SIZE,
      });
      setUsers(res.users);
      setUsersTotalCount(res.totalCount);
      setUsersTotalPages(res.totalCount === 0 ? 1 : Math.max(1, res.totalPages));
      setUsersHasNext(res.hasNextPage);
      setUsersHasPrev(res.hasPreviousPage);
    } catch {
      setError('Failed to load users');
    } finally {
      setIsLoadingUsers(false);
    }
  }, [usersPage]);

  useEffect(() => {
    void loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    if (usersTotalPages > 0 && usersPage > usersTotalPages) {
      setUsersPage(usersTotalPages);
    }
  }, [usersTotalPages, usersPage]);

  const handlePromoteToAdmin = async (userId: string) => {
    try {
      await adminService.promoteToAdmin(userId);
      await loadUsers(); // Reload users list
      setError(null);
    } catch {
      setError('Failed to promote user to admin');
    }
  };

  const handleDemoteFromAdmin = async (userId: string) => {
    try {
      await adminService.demoteFromAdmin(userId);
      await loadUsers(); // Reload users list
      setError(null);
    } catch {
      setError('Failed to demote user from admin');
    }
  };

  const handleToggleUserStatus = async (userId: string) => {
    try {
      await adminService.toggleUserStatus(userId);
      await loadUsers(); // Reload users list
      setError(null);
    } catch {
      setError('Failed to toggle user status');
    }
  };

  const openGrantVipModal = (target: User) => {
    setVipModalUser(target);
    setVipDaysInput('30');
    setVipModalError(null);
  };

  const closeGrantVipModal = () => {
    if (vipModalSubmitting) return;
    setVipModalUser(null);
    setVipModalError(null);
  };

  const handleGrantVipSubmit = async () => {
    if (!vipModalUser) return;
    const days = Number.parseInt(vipDaysInput, 10);
    if (!Number.isFinite(days) || days < 1) {
      setVipModalError('Informe ao menos 1 dia.');
      return;
    }
    setVipModalSubmitting(true);
    setVipModalError(null);
    try {
      await adminService.grantVipDays(vipModalUser.id, days);
      setVipModalUser(null);
      await loadUsers();
      setError(null);
    } catch {
      setVipModalError('Falha ao conceder VIP. Tente novamente.');
    } finally {
      setVipModalSubmitting(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    localStorage.removeItem('userId');
    window.location.href = '/';
  };

  const handleBackToHome = () => {
    navigate('/');
  };

  const handleQuizSelect = async (quiz: Quiz) => {
    try {
      const full = await adminService.getQuizById(quiz.id);
      setSelectedQuiz(full);
    } catch {
      setSelectedQuiz(quiz);
    }
  };

  const handleQuizUpdatedFromQuestions = (quiz: Quiz) => {
    setSelectedQuiz(quiz);
  };

  const handleBackToQuizzes = () => {
    setSelectedQuiz(null);
  };

  if (isLoading) {
    return (
      <div className="admin-loading">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="admin-loading">
        <div className="text-center">
          <p>User not found. Redirecting to login...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <Navbar bg="dark" variant="dark" expand="lg" className="admin-navbar">
        <Container fluid>
          <Navbar.Brand as={Link} to="/" className="navbar-brand">
            <i className="bi bi-shield-lock-fill me-2"></i>
            Quizter Club Admin
          </Navbar.Brand>
          
          <Navbar.Toggle aria-controls="basic-navbar-nav" />
          <Navbar.Collapse id="basic-navbar-nav">
            <div className="ms-auto">
              <Button variant="outline-light" type="button" onClick={handleBackToHome} className="me-2">
                <i className="bi bi-arrow-left me-1"></i>
                Back to Home
              </Button>
              <Button variant="outline-danger" onClick={handleLogout}>
                <i className="bi bi-box-arrow-right me-1"></i>
                Logout
              </Button>
            </div>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      <Container fluid className="admin-content">
        <Row className="mb-4">
          <Col>
            <div className="admin-header">
              <h1>Admin Panel</h1>
              <p className="text-muted">Welcome back, {user.nickname || user.name}!</p>
            </div>
          </Col>
        </Row>

        <Row>
          <Col>
            <Card className="admin-card">
              <Card.Body>
                <Tab.Container id="admin-tabs" defaultActiveKey="dashboard">
                  <Row>
                    <Col md={3}>
                      <Nav variant="pills" className="flex-column admin-nav">
                        <Nav.Item>
                          <Nav.Link eventKey="dashboard">
                            <i className="bi bi-speedometer2 me-2"></i>
                            Dashboard
                          </Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                          <Nav.Link eventKey="users">
                            <i className="bi bi-people me-2"></i>
                            Users
                          </Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                          <Nav.Link eventKey="quizzes">
                            <i className="bi bi-question-circle me-2"></i>
                            Quizzes
                          </Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                          <Nav.Link eventKey="achievements">
                            <i className="bi bi-trophy me-2"></i>
                            Achievements
                          </Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                          <Nav.Link eventKey="career">
                            <i className="bi bi-signpost-2 me-2"></i>
                            Career missions
                          </Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                          <Nav.Link eventKey="daily-missions">
                            <i className="bi bi-calendar-check me-2"></i>
                            Daily missions
                          </Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                          <Nav.Link eventKey="notifications">
                            <i className="bi bi-megaphone me-2"></i>
                            Notifications
                          </Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                          <Nav.Link eventKey="reports">
                            <i className="bi bi-graph-up me-2"></i>
                            Reports
                          </Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                          <Nav.Link eventKey="settings">
                            <i className="bi bi-gem me-2"></i>
                            VIP pricing
                          </Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                          <Nav.Link eventKey="scraper">
                            <i className="bi bi-cloud-download me-2"></i>
                            Scraper
                          </Nav.Link>
                        </Nav.Item>
                      </Nav>
                    </Col>
                    <Col md={9}>
                      <Tab.Content>
                        <Tab.Pane eventKey="dashboard">
                          <div className="admin-tab-content">
                            <h3>Admin Dashboard</h3>
                            <p>Welcome to the admin dashboard. Here you can manage users, quizzes, and view reports.</p>
                            
                            {error && (
                              <Alert variant="danger" dismissible onClose={() => setError(null)}>
                                {error}
                              </Alert>
                            )}
                            
                            {dashboardData && (
                              <Row className="mt-4">
                                                              <Col md={3}>
                                <Card className="admin-stats-card">
                                  <Card.Body>
                                    <Card.Title>Total Users</Card.Title>
                                    <Card.Text className="stats-number">{dashboardData.totalUsers}</Card.Text>
                                  </Card.Body>
                                </Card>
                              </Col>
                              <Col md={3}>
                                <Card className="admin-stats-card">
                                  <Card.Body>
                                    <Card.Title>Active Users</Card.Title>
                                    <Card.Text className="stats-number">{dashboardData.activeUsers}</Card.Text>
                                  </Card.Body>
                                </Card>
                              </Col>
                              <Col md={3}>
                                <Card className="admin-stats-card">
                                  <Card.Body>
                                    <Card.Title>Admin Users</Card.Title>
                                    <Card.Text className="stats-number">{dashboardData.adminUsers}</Card.Text>
                                  </Card.Body>
                                </Card>
                              </Col>
                              <Col md={3}>
                                <Card className="admin-stats-card">
                                  <Card.Body>
                                    <Card.Title>VIP Users</Card.Title>
                                    <Card.Text className="stats-number">{dashboardData.vipUsers}</Card.Text>
                                  </Card.Body>
                                </Card>
                              </Col>
                              </Row>
                            )}
                          </div>
                        </Tab.Pane>
                        
                        <Tab.Pane eventKey="users">
                          <div className="admin-tab-content">
                            <h3>User Management</h3>
                            <p>Manage user accounts, permissions, and view user statistics.</p>
                            
                            {error && (
                              <Alert variant="danger" dismissible onClose={() => setError(null)}>
                                {error}
                              </Alert>
                            )}
                            
                            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                              <Button variant="primary" onClick={loadUsers} disabled={isLoadingUsers}>
                                <i className="bi bi-arrow-clockwise me-2"></i>
                                {isLoadingUsers ? 'Loading...' : 'Refresh Users'}
                              </Button>
                              {usersTotalCount > 0 ? (
                                <span className="small text-muted">
                                  Total users: {usersTotalCount}
                                  {usersTotalPages > 1
                                    ? ` · Page ${usersPage} of ${usersTotalPages}`
                                    : ''}
                                </span>
                              ) : null}
                            </div>
                            
                            {usersTotalCount > 0 ? (
                              <Card>
                                <Card.Body>
                                  <Table responsive striped hover>
                                    <thead>
                                      <tr>
                                        <th>Name</th>
                                        <th>Email</th>
                                        <th>Nickname</th>
                                        <th>Type</th>
                                        <th>Level</th>
                                        <th>Status</th>
                                        <th>Actions</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {users.map((user) => (
                                        <tr key={user.id}>
                                          <td>{user.name}</td>
                                          <td>{user.email}</td>
                                          <td>{user.nickname || 'Not set'}</td>
                                          <td>
                                            <Badge bg={
                                              user.userType === 'Admin' ? 'danger' :
                                              user.userType === 'Vip' || user.userType === 'Premium' ? 'warning' : 'secondary'
                                            }>
                                              {user.userType}
                                            </Badge>
                                          </td>
                                          <td>{user.level}</td>
                                          <td>
                                            <Badge bg={user.isActive ? 'success' : 'danger'}>
                                              {user.isActive ? 'Active' : 'Inactive'}
                                            </Badge>
                                          </td>
                                          <td>
                                            <div className="btn-group" role="group">
                                              {user.userType !== 'Admin' ? (
                                                <Button
                                                  size="sm"
                                                  variant="outline-danger"
                                                  onClick={() => handlePromoteToAdmin(user.id)}
                                                  title="Promote to Admin"
                                                >
                                                  <i className="bi bi-shield-check"></i>
                                                </Button>
                                              ) : (
                                                <Button
                                                  size="sm"
                                                  variant="outline-warning"
                                                  onClick={() => handleDemoteFromAdmin(user.id)}
                                                  title="Demote from Admin"
                                                >
                                                  <i className="bi bi-shield-x"></i>
                                                </Button>
                                              )}
                                              {user.userType !== 'Admin' && (
                                                <Button
                                                  size="sm"
                                                  variant="outline-warning"
                                                  onClick={() => openGrantVipModal(user)}
                                                  title="Grant VIP days"
                                                >
                                                  <i className="bi bi-gem"></i>
                                                </Button>
                                              )}
                                              <Button
                                                size="sm"
                                                variant="outline-secondary"
                                                onClick={() => handleToggleUserStatus(user.id)}
                                                title={user.isActive ? 'Deactivate User' : 'Activate User'}
                                              >
                                                <i className={`bi ${user.isActive ? 'bi-person-x' : 'bi-person-check'}`}></i>
                                              </Button>
                                            </div>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </Table>
                                  {usersTotalPages > 1 && (
                                    <div className="d-flex justify-content-center mt-3">
                                      <Pagination className="mb-0 flex-wrap justify-content-center">
                                        <Pagination.First
                                          disabled={!usersHasPrev}
                                          onClick={() => setUsersPage(1)}
                                        />
                                        <Pagination.Prev
                                          disabled={!usersHasPrev}
                                          onClick={() => setUsersPage((p) => Math.max(1, p - 1))}
                                        />
                                        {Array.from({ length: Math.min(5, usersTotalPages) }, (_, i) => {
                                          const pageNum =
                                            Math.max(1, Math.min(usersTotalPages - 4, usersPage - 2)) + i;
                                          if (pageNum > usersTotalPages) return null;
                                          return (
                                            <Pagination.Item
                                              key={pageNum}
                                              active={pageNum === usersPage}
                                              onClick={() => setUsersPage(pageNum)}
                                            >
                                              {pageNum}
                                            </Pagination.Item>
                                          );
                                        })}
                                        <Pagination.Next
                                          disabled={!usersHasNext}
                                          onClick={() =>
                                            setUsersPage((p) => Math.min(usersTotalPages, p + 1))
                                          }
                                        />
                                        <Pagination.Last
                                          disabled={!usersHasNext}
                                          onClick={() => setUsersPage(usersTotalPages)}
                                        />
                                      </Pagination>
                                    </div>
                                  )}
                                </Card.Body>
                              </Card>
                            ) : (
                              <Card>
                                <Card.Body>
                                  <p className="text-muted text-center">
                                    {isLoadingUsers ? 'Loading users...' : 'No users found.'}
                                  </p>
                                </Card.Body>
                              </Card>
                            )}
                          </div>
                        </Tab.Pane>
                        
                        <Tab.Pane eventKey="quizzes">
                          <div className="admin-tab-content">
                            {selectedQuiz ? (
                              <QuestionManager 
                                quiz={selectedQuiz} 
                                onBack={handleBackToQuizzes}
                                onQuizUpdated={handleQuizUpdatedFromQuestions}
                              />
                            ) : (
                              <AdminQuizList onQuizSelect={handleQuizSelect} />
                            )}
                          </div>
                        </Tab.Pane>

                        <Tab.Pane eventKey="achievements">
                          <AdminAchievements />
                        </Tab.Pane>

                        <Tab.Pane eventKey="career">
                          <AdminCareerMissions />
                        </Tab.Pane>

                        <Tab.Pane eventKey="daily-missions">
                          <AdminDailyMissions />
                        </Tab.Pane>

                        <Tab.Pane eventKey="notifications">
                          <AdminBroadcastPanel />
                        </Tab.Pane>
                        
                        <Tab.Pane eventKey="reports">
                          <div className="admin-tab-content">
                            <AdminReports />
                          </div>
                        </Tab.Pane>
                        
                        <Tab.Pane eventKey="settings">
                          <AdminVipPricing />
                        </Tab.Pane>

                        <Tab.Pane eventKey="scraper">
                          <AdminPokemonScraper />
                        </Tab.Pane>
                      </Tab.Content>
                    </Col>
                  </Row>
                </Tab.Container>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      <Modal show={vipModalUser !== null} onHide={closeGrantVipModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-gem me-2 text-warning"></i>
            Conceder VIP
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {vipModalUser && (
            <p className="mb-3">
              Concedendo VIP para{' '}
              <strong>
                {vipModalUser.nickname || vipModalUser.name || vipModalUser.email}
              </strong>
              .
            </p>
          )}
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              void handleGrantVipSubmit();
            }}
          >
            <Form.Group controlId="grantVipDays">
              <Form.Label>Quantos dias?</Form.Label>
              <Form.Control
                type="number"
                min={1}
                step={1}
                value={vipDaysInput}
                onChange={(e) => setVipDaysInput(e.target.value)}
                autoFocus
                disabled={vipModalSubmitting}
              />
              <Form.Text className="text-muted">
                Mínimo de 1 dia. Acumula com o tempo de VIP restante.
              </Form.Text>
            </Form.Group>
            {vipModalError && (
              <Alert variant="danger" className="mt-3 mb-0">
                {vipModalError}
              </Alert>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={closeGrantVipModal}
            disabled={vipModalSubmitting}
          >
            Cancelar
          </Button>
          <Button
            variant="warning"
            onClick={() => void handleGrantVipSubmit()}
            disabled={vipModalSubmitting}
          >
            {vipModalSubmitting ? 'Aplicando...' : 'OK'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AdminPanel; 